import { keepPreviousData, useQuery } from "@tanstack/react-query";
import Translate from "component/global/translation/translation-component";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PortalFrameComponent from "../components/PortalFrame";
import { useGatePortalCache } from "../components/PortalFrameCache";
import { useEffect, useMemo, useRef } from "react";
import { GateLocationSnapshot, mergeGateWidgets, normalizeGateDefinition, normalizeGateWidgetDefinition } from "modules/gate/types/gate";
import { resolveGateManifestSlug, useGatePwaManifest } from "modules/gate/hooks/useGatePwaManifest";
import { getGateBootstrap, getGateLandingBootstrap } from "modules/gate/services/gateBootstrapApi";

interface PortalPageProps {
    page?: string;
    publicOnly?: boolean;
    landing?: boolean;
}

const performanceMark = (name: string) => {
    if (typeof performance !== "undefined" && performance.mark) {
        performance.mark(name);
    }
};

const performanceMeasure = (name: string, startMark: string) => {
    if (typeof performance === "undefined" || !performance.measure) return;

    try {
        performance.measure(name, startMark);
    } catch (_error) {
        // Performance marks are diagnostic only.
    }
};

export const PortalPage = ({ page, publicOnly = false, landing = false }: PortalPageProps) => {
    const routeParams = useParams();
    const { pageId } = routeParams;
    const routeWildcard = routeParams["*"];
    const location = useLocation();
    const navigate = useNavigate();
    const portalCache = useGatePortalCache();
    const currentPage = useMemo(() => page || pageId || "", [page, pageId]);
    const hostLocation = useMemo<GateLocationSnapshot>(() => {
        const queryParams = new URLSearchParams(location.search);
        const query: Record<string, string | string[]> = {};
        const routePath = routeWildcard ? `/${routeWildcard.replace(/^\/+/, "")}` : "/";

        queryParams.forEach((value, key) => {
            const currentValue = query[key];
            if (currentValue === undefined) {
                query[key] = value;
                return;
            }
            query[key] = Array.isArray(currentValue) ? [...currentValue, value] : [currentValue, value];
        });

        return {
            origin: window.location.origin,
            href: window.location.href,
            pathname: location.pathname,
            routePath,
            search: location.search,
            hash: location.hash,
            params: Object.entries(routeParams).reduce<Record<string, string>>((accumulator, [key, value]) => {
                if (value) accumulator[key] = value;
                return accumulator;
            }, {}),
            query,
        };
    }, [location.hash, location.pathname, location.search, routeParams, routeWildcard]);

    const bootstrapQuery = useQuery({
        queryKey: ["gate-bootstrap", landing ? "landing" : publicOnly ? "public" : "private", currentPage],
        queryFn: async () => {
            const metric = landing ? "landing" : currentPage;
            const startMark = `primum.gate.bootstrap.${metric}.start`;
            performanceMark(startMark);

            try {
                return landing
                    ? await getGateLandingBootstrap()
                    : await getGateBootstrap(currentPage, publicOnly);
            } finally {
                performanceMeasure(`primum.gate.bootstrap.${metric}`, startMark);
            }
        },
        enabled: landing || Boolean(currentPage),
        retry: false,
        staleTime: 1000 * 60 * 5,
        placeholderData: keepPreviousData,
    });

    const data = useMemo(() => {
        if (!bootstrapQuery.data?.page) return null;

        const definition = normalizeGateDefinition(bootstrapQuery.data.page);
        const widgets = (bootstrapQuery.data.widgets || []).map(normalizeGateWidgetDefinition);

        return {
            ...definition,
            widgets: mergeGateWidgets(definition.widgetRefs || [], widgets),
        };
    }, [bootstrapQuery.data]);

    const lastResolvedLocationRef = useRef(hostLocation);
    if (!bootstrapQuery.isPlaceholderData && data) {
        lastResolvedLocationRef.current = hostLocation;
    }

    const manifestSlug = data ? resolveGateManifestSlug(currentPage, data.slug) : undefined;
    useGatePwaManifest(manifestSlug);

    const resolvedHostLocation = bootstrapQuery.isPlaceholderData ? lastResolvedLocationRef.current : hostLocation;
    const cacheKey = data ? data.id || data.slug : "";
    const frameProps = useMemo(() => data ? {
        page: data,
        hostLocation: resolvedHostLocation,
        navigate,
    } : null, [data, navigate, resolvedHostLocation]);

    useEffect(() => {
        if (!portalCache) return;

        if (bootstrapQuery.isError) {
            portalCache.clearActive();
            return;
        }

        if (!cacheKey || !frameProps) return;
        portalCache.registerActive(cacheKey, frameProps);
    }, [bootstrapQuery.isError, cacheKey, frameProps, portalCache]);

    if (bootstrapQuery.isError) return <Translate text="Not found" />;
    if (!data) return null;

    if (portalCache) return null;

    return (
        <PortalFrameComponent
            page={data}
            hostLocation={resolvedHostLocation}
            navigate={navigate}
        />
    );
};
